import { Building2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtDate, fmtMoney } from '../shared/format'
import { useCategories } from './api'
import { useAssignDepartment, useCategoryDepartments, useFinanceDepartments, useSetCategoryDepartment, useUnassigned } from './departments'
import { toastError } from './ui'

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })
const yearStart = () => `${new Date().getFullYear()}-01-01`

/** Departman: eski gider kayıtlarına / kasa çıkışlarına toplu atama ve kategori varsayılan departmanları. */
export function DepartmentsPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'assign' | 'categories'>('assign')
  return (
    <>
      <PageHeader eyebrow={t('nav.groups.finance')} title={t('finance.departments.title')} description={t('finance.departments.subtitle')} />
      <div className="space-y-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'assign' | 'categories')}>
          <TabsList size="sm">
            <TabsTrigger value="assign">{t('finance.departments.tabs.assign')}</TabsTrigger>
            <TabsTrigger value="categories">{t('finance.departments.tabs.categories')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'assign' ? <BulkAssign /> : <CategoryDefaults />}
      </div>
    </>
  )
}

function BulkAssign() {
  const { t } = useTranslation()
  const [kind, setKind] = useState<'ledger' | 'cash'>('ledger')
  const [from, setFrom] = useState(yearStart)
  const [to, setTo] = useState(today)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [dept, setDept] = useState('')
  const q = useUnassigned(kind, from, to)
  const departments = useFinanceDepartments()
  const assign = useAssignDepartment()
  const rows = q.data ?? []
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const allOn = rows.length > 0 && rows.every((r) => picked.has(r.id))
  const selectedTotal = rows.filter((r) => picked.has(r.id)).reduce((n, r) => n + Number(r.amount), 0)
  return (
    <section className="space-y-3 rounded-xl border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-end gap-2">
        <Tabs
          value={kind}
          onValueChange={(v) => {
            setKind(v as 'ledger' | 'cash')
            setPicked(new Set())
          }}
        >
          <TabsList size="sm">
            <TabsTrigger value="ledger">{t('finance.departments.kinds.ledger')}</TabsTrigger>
            <TabsTrigger value="cash">{t('finance.departments.kinds.cash')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="w-40">
          <DateField aria-label={t('finance.departments.from')} value={from} onChange={setFrom} clearable={false} />
        </div>
        <div className="w-40">
          <DateField aria-label={t('finance.departments.to')} value={to} onChange={setTo} clearable={false} />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select className="w-52" aria-label={t('finance.departments.field')} value={dept} onChange={setDept} options={[{ value: '', label: t('common.select') }, ...(departments.data ?? []).map((d) => ({ value: d.id, label: d.name }))]} />
          <Button
            disabled={!dept || !picked.size || assign.isPending}
            onClick={() =>
              assign.mutate(
                { kind, ids: [...picked], departmentId: dept },
                {
                  onSuccess: (r) => {
                    toast.success(t('finance.departments.assigned', { count: r?.assigned ?? picked.size }))
                    setPicked(new Set())
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('finance.departments.assign', { count: picked.size })}
          </Button>
        </div>
      </div>
      {picked.size ? <p className="text-xs text-muted-foreground">{t('finance.departments.selected', { count: picked.size, amount: fmtMoney(selectedTotal.toFixed(2)) })}</p> : null}
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isLoading ? (
        <Skeleton className="h-40" />
      ) : !rows.length ? (
        <EmptyState icon={Building2} title={t('finance.departments.noneLeft')} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH className="w-8">
                  <input
                    type="checkbox"
                    aria-label={t('finance.departments.selectAll')}
                    className="size-4 accent-[var(--color-primary)]"
                    checked={allOn}
                    onChange={() => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.id)))}
                  />
                </TH>
                <TH>{t('finance.tx.date')}</TH>
                <TH>{t('finance.tx.description')}</TH>
                <TH>{t('finance.category')}</TH>
                <TH className="text-right">{t('finance.reimbursements.amount')}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => (
                <TR key={r.id} className="cursor-pointer" onClick={() => toggle(r.id)}>
                  <TD>
                    <input type="checkbox" aria-label={r.description} className="size-4 accent-[var(--color-primary)]" checked={picked.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} />
                  </TD>
                  <TD className="whitespace-nowrap tabular-nums">{fmtDate(r.date)}</TD>
                  <TD className="max-w-96 truncate">{r.description || '—'}</TD>
                  <TD>{r.category || '—'}</TD>
                  <TD className="text-right tabular-nums">{fmtMoney(r.amount)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function CategoryDefaults() {
  const { t } = useTranslation()
  const categories = useCategories()
  const defaults = useCategoryDepartments()
  const departments = useFinanceDepartments()
  const set = useSetCategoryDepartment()
  const items = (categories.data ?? []).filter((c) => c.kind === 'expense' && c.active)
  return (
    <section className="space-y-2 rounded-xl border bg-card p-4 shadow-card">
      <p className="text-sm text-muted-foreground">{t('finance.departments.categoryHint')}</p>
      {categories.isLoading || defaults.isLoading ? (
        <Skeleton className="h-40" />
      ) : (
        <ul className="divide-y">
          {items.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1 text-sm">{c.name}</span>
              <Select
                className="w-56"
                aria-label={c.name}
                value={defaults.data?.[c.id] ?? ''}
                onChange={(departmentId) => set.mutate({ categoryId: c.id, departmentId }, { onSuccess: () => toast.success(t('finance.departments.categorySaved')), onError: toastError })}
                options={[{ value: '', label: t('finance.departments.noDefault') }, ...(departments.data ?? []).map((d) => ({ value: d.id, label: d.name }))]}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
